'use client'

import { parseDate, type DateValue } from '@internationalized/date'
import { Input, type InputProps } from '@nextui-org/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { CustomCalendar } from './customCalendar'

type Props = {
  lang?: string
  label: string
  value?: string
  minDate?: DateValue
  maxDate?: DateValue
  errorMessage?: any
  isDateUnavailable?: (date: DateValue) => boolean
  onChange?: (value: DateValue | undefined) => void


  /** Trigger Input overrides, spread last. Omit for the themed default look. */
  inputProps?: Partial<InputProps>
}

/**
 * Callers disagree: redeem holds `YYYY-MM-DD`, the booking forms hold `yyyyMMdd` (`booking.tsx`
 * L177, `promotionBookingForm.tsx` L421). Both accepted; anything else counts as unset.
 */
const normalizeDateString = (value?: string): string => {
  if (!value) return ''

  const dashed = value.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dashed)) return dashed

  const compact = value.slice(0, 8)
  if (/^\d{8}$/.test(compact)) {
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6)}`
  }

  return ''
}

const dateValueToString = (date: DateValue | undefined): string | undefined => {
  if (!date) {
    return undefined
  }
  const month = String(date.month).padStart(2, '0')
  const day = String(date.day).padStart(2, '0')
  return `${date.year}-${month}-${day}`
}

export function DatePicker({
  label,
  lang,
  value,
  minDate,
  maxDate,
  errorMessage,
  isDateUnavailable,
  onChange,
  inputProps
}: Props) {
  const [internalValue, setInternalValue] = useState('')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Mirrors `value` both ways, not only on clear. One-way meant a seeded field — `?date=` prefill,
  // restored draft — set the form value but left the trigger blank.
  useEffect(() => {
    setInternalValue(normalizeDateString(value))
  }, [value])

  const handleDateSelect = (dateValue: DateValue) => {
    const dateString = dateValueToString(dateValue)
    if (dateString) {
      setInternalValue(dateString)
      if (onChange) {
        onChange(dateValue)
      }
    }
  }
  const handleCalendarClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCalendarOpen(true)
  }

  const handleCalendarTouch = (e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCalendarOpen(true)
  }

  const handleReset = () => {
    onChange?.(undefined)
    setIsCalendarOpen(false)
  }

  const displayValue = useMemo(() => {
    if (!internalValue) return ''
    try {
      const date = parseDate(internalValue)
      const jsDate = new Date(date.year, date.month - 1, date.day)
      return jsDate.toLocaleDateString(lang || 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return internalValue
    }
  }, [internalValue, lang])

  return (
    <>
      {' '}
      <div
        className='group z-10 flex w-full flex-col subpixel-antialiased aria-expanded:scale-[0.97] aria-expanded:opacity-70 data-[hidden=true]:hidden'
        onClick={handleCalendarClick}
        onTouchEnd={handleCalendarTouch}
        style={{ WebkitTapHighlightColor: 'transparent' }}>
        <Input
          ref={inputRef}
          type='text'
          readOnly
          classNames={{
            label: 'text-theme/90',
            input: `text-left text-sm font-bold !text-theme cursor-pointer ${!internalValue ? 'text-transparent' : ''}`,
            inputWrapper:
              'rounded !bg-theme-200/20 shadow-none hover:!bg-theme-200/25 cursor-pointer'
          }}
          size='sm'
          radius='none'
          labelPlacement='inside'
          label={label}
          value={displayValue}
          isRequired
          errorMessage={errorMessage}
          // placeholder='Select date'
          style={{
            colorScheme: 'light'
          }}
          {...inputProps}
        />
      </div>{' '}
      <CustomCalendar
        locale={lang}
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        onReset={handleReset}
        minDate={minDate}
        maxDate={maxDate}
        isDateUnavailable={isDateUnavailable}
        onDateSelect={handleDateSelect}
        currentValue={internalValue}
      />
    </>
  )
}
